import { useState, useEffect } from 'react';
import { Search, Download, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface LogEntry {
  id: string;
  timestamp: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number;
  ipAddress: string;
  userAgent?: string;
}

export default function ApiLogsExplorer() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'error'>('all');
  const [endpointFilter, setEndpointFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/dashboard/logs');
      const data = await response.json();

      if (response.ok) {
        setLogs(data.logs || []);
      } else {
        setError(data.message || 'Failed to load logs');
      }
    } catch (err) {
      setError('Failed to connect to the server');
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      searchTerm === '' ||
      log.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ipAddress.includes(searchTerm);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'success' && log.statusCode < 400) ||
      (statusFilter === 'error' && log.statusCode >= 400);

    const matchesEndpoint =
      endpointFilter === 'all' || log.endpoint === endpointFilter;

    return matchesSearch && matchesStatus && matchesEndpoint;
  });

  const uniqueEndpoints = Array.from(new Set(logs.map((log) => log.endpoint)));

  const exportLogs = () => {
    const csv = [
      'Timestamp,Endpoint,Method,Status,Response Time (ms),IP Address',
      ...filteredLogs.map(
        (log) =>
          `${log.timestamp},${log.endpoint},${log.method},${log.statusCode},${log.responseTime},${log.ipAddress}`
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-logs-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-coral-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 dark:text-dark-muted">Loading API logs...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 dark:text-dark-text font-semibold mb-2">Failed to Load Logs</p>
          <p className="text-gray-600 dark:text-dark-muted mb-4">{error}</p>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (logs.length === 0) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-900 dark:text-dark-text font-semibold mb-2">No API Logs Yet</p>
          <p className="text-gray-600 dark:text-dark-muted">
            Your API request logs will appear here once you start making requests.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-1">
            API Logs Explorer
          </h3>
          <p className="text-sm text-gray-500 dark:text-dark-muted">
            Your API request history and debugging
          </p>
        </div>
        <button
          onClick={exportLogs}
          disabled={filteredLogs.length === 0}
          className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search endpoint or IP..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-coral-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-coral-500"
        >
          <option value="all">All Status</option>
          <option value="success">Success Only</option>
          <option value="error">Errors Only</option>
        </select>

        <select
          value={endpointFilter}
          onChange={(e) => setEndpointFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-coral-500"
        >
          <option value="all">All Endpoints</option>
          {uniqueEndpoints.map((endpoint) => (
            <option key={endpoint} value={endpoint}>
              {endpoint.replace('/api/convert/', '')}
            </option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="mb-3 text-sm text-gray-600 dark:text-dark-muted">
        Showing {filteredLogs.length} of {logs.length} logs
      </div>

      {/* Logs table */}
      <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 dark:border-dark-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-dark-bg sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-dark-text">
                Status
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-dark-text">
                Endpoint
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-dark-text">
                Time
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-dark-text">
                Response
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-dark-text">
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
            {filteredLogs.map((log) => (
              <tr
                key={log.id}
                className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {log.statusCode < 400 ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span
                      className={`text-xs font-medium ${
                        log.statusCode < 400
                          ? 'text-green-700 dark:text-green-400'
                          : 'text-red-700 dark:text-red-400'
                      }`}
                    >
                      {log.statusCode}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs bg-gray-100 dark:bg-dark-bg px-2 py-1 rounded text-gray-900 dark:text-dark-text">
                    {log.endpoint.replace('/api/convert/', '')}
                  </code>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-dark-muted">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(log.timestamp), {
                      addSuffix: true,
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs font-medium ${
                      log.responseTime < 100
                        ? 'text-green-600'
                        : log.responseTime < 300
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}
                  >
                    {log.responseTime}ms
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-600 dark:text-dark-muted font-mono">
                    {log.ipAddress}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
